import { Navigate, NavLink, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/v2/lib/rbac";
import { GlassCard } from "@/v2/admin/ui";
import AdminAnalytics from "@/v2/admin/Analytics";
import AdminEmployees from "@/v2/admin/Employees";
import AdminJoinRequests from "@/v2/admin/JoinRequests";
import AdminDepartments from "@/v2/admin/Departments";
import AdminCompetencies from "@/v2/admin/Competencies";
import AdminPaths from "@/v2/admin/Paths";
import AdminChallenges from "@/v2/admin/Challenges";
import AdminVideos from "@/v2/admin/Videos";
import AdminFiles from "@/v2/admin/Files";
import AdminQuizzes from "@/v2/admin/Quizzes";
import AdminResults from "@/v2/admin/Results";
import AdminPoints from "@/v2/admin/Points";
import AdminReports from "@/v2/admin/Reports";
import {
  UserPlus, Users, Building2, Target, Route, Swords, Clapperboard, Paperclip,
  FileQuestion, BarChart3, ClipboardList, Coins, FileText,
} from "lucide-react";

const PEOPLE = [
  { key: "requests", label: "طلبات الانضمام", icon: UserPlus, perm: "manage_users", component: AdminJoinRequests },
  { key: "employees", label: "الموظفون", icon: Users, perm: "manage_users", component: AdminEmployees },
  { key: "departments", label: "الإدارات", icon: Building2, perm: "manage_users", component: AdminDepartments },
];

const CONTENT = [
  { key: "competencies", label: "الجدارات", icon: Target, perm: "manage_content", component: AdminCompetencies },
  { key: "paths", label: "المسارات", icon: Route, perm: "manage_content", component: AdminPaths },
  { key: "challenges", label: "التحديات", icon: Swords, perm: "manage_content", component: AdminChallenges },
  { key: "videos", label: "الفيديوهات", icon: Clapperboard, perm: "manage_content", component: AdminVideos },
  { key: "files", label: "الملفات", icon: Paperclip, perm: "manage_content", component: AdminFiles },
  { key: "quizzes", label: "الاختبارات", icon: FileQuestion, perm: "manage_content", component: AdminQuizzes },
];

const PERFORMANCE = [
  { key: "analytics", label: "التحليلات", icon: BarChart3, perm: "view_reports", component: AdminAnalytics },
  { key: "results", label: "النتائج", icon: ClipboardList, perm: "manage_users", component: AdminResults },
  { key: "points", label: "النقاط", icon: Coins, perm: "manage_points", component: AdminPoints },
  { key: "reports", label: "التقارير", icon: FileText, perm: "view_reports", component: AdminReports },
];

function ControlHub({ base, title, description, tabs }) {
  const { user } = useAuth();
  const { section } = useParams();
  const visible = tabs.filter((tab) => can(user, tab.perm));
  if (!visible.length) return <Navigate to="/admin" replace />;
  if (!section) return <Navigate to={`/admin/${base}/${visible[0].key}`} replace />;

  const active = visible.find((tab) => tab.key === section);
  if (!active) return <Navigate to={`/admin/${base}/${visible[0].key}`} replace />;
  const ActivePage = active.component;

  return (
    <div className="space-y-5" data-testid={`admin-${base}-hub`}>
      <GlassCard className="p-4 sm:p-5">
        <div className="mb-4">
          <h1 className="text-lg sm:text-xl font-extrabold">{title}</h1>
          <p className="text-xs sm:text-sm text-[#bcc8cf] mt-1">{description}</p>
        </div>
        <nav className="flex gap-2 overflow-x-auto pb-1" aria-label={title}>
          {visible.map((tab) => (
            <NavLink
              key={tab.key}
              to={`/admin/${base}/${tab.key}`}
              data-testid={`${base}-tab-${tab.key}`}
              className={({ isActive }) =>
                `shrink-0 inline-flex items-center gap-2 h-10 px-4 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                  isActive
                    ? "bg-[#91cab8]/18 border border-[#91cab8]/35 text-white shadow-[0_8px_30px_rgba(145,202,184,.12)]"
                    : "bg-white/[.035] border border-white/10 text-[#bcc8cf] hover:text-white hover:bg-white/[.07]"
                }`
              }
            >
              <tab.icon size={15} /> {tab.label}
            </NavLink>
          ))}
        </nav>
      </GlassCard>
      <ActivePage />
    </div>
  );
}

export const PeopleHub = () => (
  <ControlHub
    base="people"
    title="إدارة الأفراد والهيكل التنظيمي"
    description="اعتماد الموظفين وإدارة بياناتهم وإداراتهم من مساحة واحدة."
    tabs={PEOPLE}
  />
);

export const ContentHub = () => (
  <ControlHub
    base="content"
    title="استوديو المحتوى التدريبي"
    description="ابنِ الرحلة كاملة من الجدارة إلى المسار والتحدي والفيديو والاختبار."
    tabs={CONTENT}
  />
);

export const PerformanceHub = () => (
  <ControlHub
    base="performance"
    title="الأداء والقياس"
    description="تابع النتائج والنقاط والمؤشرات والتقارير وفق صلاحيتك."
    tabs={PERFORMANCE}
  />
);
